import React from "react";
import {
  AbsoluteFill,
  Easing,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
} from "remotion";

const V = {
  night: "#050816",
  white: "#F7F8FF",
  cyan: "#62E7FF",
  blue: "#5269FF",
  violet: "#9C6CFF",
  pink: "#FF65BF",
  lime: "#C8FF62",
  orange: "#FF7057",
};

const clamp = {
  extrapolateLeft: "clamp" as const,
  extrapolateRight: "clamp" as const,
};

const smooth = Easing.bezier(0.16, 1, 0.3, 1);
const travel = Easing.bezier(0.65, 0, 0.35, 1);

const p = (
  frame: number,
  from: number,
  to: number,
  easing: (value: number) => number = smooth,
) =>
  interpolate(frame, [from, to], [0, 1], {
    ...clamp,
    easing,
  });

const sceneOpacity = (
  frame: number,
  enter: [number, number],
  exit: [number, number],
) =>
  interpolate(frame, [...enter, ...exit], [0, 1, 1, 0], {
    ...clamp,
    easing: travel,
  });

const sceneCamera = (frame: number, duration: number) => {
  const enter = p(frame, 0, 34, travel);
  const exit = p(frame, duration - 34, duration, travel);
  const scale = interpolate(enter - exit, [-1, 0, 1], [1.08, 1, 1.06], clamp);
  const y = interpolate(enter - exit, [-1, 0, 1], [-55, 0, 50], clamp);
  const rotation = interpolate(enter - exit, [-1, 0, 1], [-1.2, 0, 0.8], clamp);
  return `translate3d(0, ${y}px, 0) scale(${scale}) rotate(${rotation}deg)`;
};

const VerticalRadar: React.FC<{frame: number}> = ({frame}) => {
  const rotation = interpolate(frame, [0, 150], [-50, 260], clamp);
  const pulse = 1 + Math.sin(frame / 8) * 0.08;

  return (
    <svg className="vh-radar" viewBox="0 0 620 620" aria-hidden="true">
      <defs>
        <linearGradient id="vh-radar-beam" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0" stopColor={V.cyan} stopOpacity="0" />
          <stop offset="1" stopColor={V.cyan} stopOpacity="0.85" />
        </linearGradient>
      </defs>
      <circle cx="310" cy="310" r="286" fill="none" stroke={V.cyan} strokeWidth="2" opacity="0.28" />
      <circle cx="310" cy="310" r="226" fill="none" stroke={V.cyan} strokeWidth="2" opacity="0.48" strokeDasharray="8 14" />
      <circle cx="310" cy="310" r="164" fill="none" stroke={V.pink} strokeWidth="2" opacity="0.6" />
      <circle cx="310" cy="310" r="94" fill="none" stroke={V.white} strokeWidth="2" opacity="0.32" strokeDasharray="5 10" />
      <g transform={`rotate(${rotation} 310 310)`}>
        <path d="M310 310 310 24 A286 286 0 0 1 536 135Z" fill="url(#vh-radar-beam)" opacity="0.48" />
        <line x1="310" y1="310" x2="310" y2="24" stroke={V.cyan} strokeWidth="3" />
      </g>
      <circle cx="310" cy="310" r={18 * pulse} fill={V.cyan} />
      <circle cx="196" cy="202" r="11" fill={V.pink} />
      <circle cx="447" cy="404" r="10" fill={V.lime} />
    </svg>
  );
};

const VerticalPhoto: React.FC<{
  src: string;
  frame: number;
  opacity?: number;
}> = ({src, frame, opacity = 0.75}) => {
  const drift = p(frame, 0, 150, travel);

  return (
    <AbsoluteFill style={{opacity}}>
      <Img
        src={staticFile(src)}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          filter: "saturate(1.12) contrast(1.06)",
          transform: `translateX(${interpolate(drift, [0, 1], [-80, 60])}px) scale(${1.48 - drift * 0.12})`,
        }}
      />
      <AbsoluteFill className="vh-photo-grade" />
    </AbsoluteFill>
  );
};

const VerticalHud: React.FC<{frame: number}> = ({frame}) => (
  <div className="vh-hud">
    <div><span>GLASS OS / VERTICAL</span><span>{String(frame).padStart(4, "0")}F</span></div>
    <div className="vh-hud-progress"><i style={{transform: `scaleX(${interpolate(frame, [0, 840], [0, 1], clamp)})`}} /></div>
    <div><span className="vh-live">LIVE</span><span>1080 × 1920 / 30 FPS</span></div>
  </div>
);

const VerticalIntro: React.FC<{frame: number}> = ({frame}) => {
  const opacity = sceneOpacity(frame, [0, 18], [120, 150]);
  const intro = spring({
    frame,
    fps: 30,
    config: {damping: 22, stiffness: 90, mass: 1},
  });
  const ring = interpolate(frame, [0, 150], [-40, 170], clamp);

  return (
    <AbsoluteFill style={{opacity, color: V.white, overflow: "hidden", transform: sceneCamera(frame, 150)}}>
      <VerticalPhoto src="backgrounds/glass-atrium.png" frame={frame} />
      <div
        className="vh-intro-ring vh-glass"
        style={{
          transform: `translate(-50%, -50%) rotate(${ring}deg) scale(${0.75 + intro * 0.25})`,
        }}
      >
        <VerticalRadar frame={frame} />
        <i /><i /><i />
      </div>
      <div
        className="vh-vertical-scan"
        style={{transform: `translateX(${interpolate(frame, [0, 150], [-120, 180], clamp)}%) rotate(12deg)`}}
      />
      <div
        className="vh-intro-copy"
        style={{
          opacity: intro,
          filter: `blur(${interpolate(intro, [0, 1], [22, 0])}px)`,
          transform: `translateY(${(1 - intro) * 100}px)`,
        }}
      >
        <span>ADVANCED VISUAL SYSTEMS</span>
        <h1>
          Design at
          <br />
          the speed
          <br />
          of <em>light.</em>
        </h1>
      </div>
      <div className="vh-data-pill vh-glass">
        <span>SIGNAL</span><strong>98.4%</strong><i />
      </div>
      <div className="vh-intro-readouts">
        <span>LAT 33.97</span><span>LONG -6.84</span><span>NODE / 047</span>
      </div>
    </AbsoluteFill>
  );
};

const VerticalCards: React.FC<{frame: number}> = ({frame}) => {
  const opacity = sceneOpacity(frame, [0, 20], [130, 160]);
  const items = [
    {title: "Adaptive", color: V.cyan, icon: "✦"},
    {title: "Responsive", color: V.pink, icon: "↗"},
    {title: "Connected", color: V.lime, icon: "◎"},
  ];

  return (
    <AbsoluteFill style={{opacity, color: V.white, overflow: "hidden", transform: sceneCamera(frame, 160)}}>
      <VerticalPhoto src="backgrounds/light-gallery.png" frame={frame} opacity={0.68} />
      <div className="vh-scene-label">02 / GLASS SYSTEM</div>
      <h2 className="vh-cards-title">Clear layers.<br /><em>Deep meaning.</em></h2>
      <div className="vh-card-stack">
        {items.map((item, index) => {
          const enter = spring({
            frame: frame - index * 8,
            fps: 30,
            config: {damping: 20, stiffness: 102},
          });

          return (
            <article
              className="vh-card vh-glass"
              key={item.title}
              style={{
                opacity: enter,
                transform: `translateX(${(1 - enter) * (index % 2 ? 160 : -160)}px) rotate(${(1 - enter) * (index % 2 ? 5 : -5)}deg)`,
              }}
            >
              <div style={{color: item.color, backgroundColor: `${item.color}22`}}>{item.icon}</div>
              <span>INTERFACE / 0{index + 1}</span>
              <strong>{item.title}</strong>
              <i style={{backgroundColor: item.color}} />
            </article>
          );
        })}
      </div>
      <div className="vh-vertical-dock vh-glass">
        {["⌘", "✦", "↗", "◎", "•••"].map((item) => <span key={item}>{item}</span>)}
      </div>
    </AbsoluteFill>
  );
};

const VerticalWaves: React.FC<{frame: number}> = ({frame}) => {
  const opacity = sceneOpacity(frame, [0, 18], [126, 160]);
  const reveal = p(frame, 8, 40);
  const core = spring({
    frame: frame - 12,
    fps: 30,
    config: {damping: 20, stiffness: 92, mass: 1},
  });
  const rotation = interpolate(frame, [0, 160], [-25, 145], clamp);
  const sweep = interpolate(frame, [0, 160], [-35, 135], clamp);
  const exit = p(frame, 118, 156, travel);

  return (
    <AbsoluteFill style={{opacity, color: V.white, overflow: "hidden", transform: sceneCamera(frame, 160)}}>
      <VerticalPhoto src="backgrounds/glass-waves.png" frame={frame} opacity={0.7} />
      <div className="vh-scene-label">03 / SIGNAL CHAMBER</div>
      <div
        className="vh-flow-heading"
        style={{
          opacity: reveal,
          filter: `blur(${interpolate(reveal, [0, 1], [18, 0])}px)`,
          transform: `translateY(${(1 - reveal) * 65}px)`,
        }}
      >
        <span>ENERGY / FREQUENCY / DIRECTION</span>
        <h2>Signal becomes<br /><em>visible.</em></h2>
      </div>

      <div
        className="vh-flow-chamber vh-glass"
        style={{
          opacity: core,
          transform: `translateY(${(1 - core) * 120}px) scale(${0.9 + core * 0.1 - exit * 0.05})`,
        }}
      >
        <div className="vh-flow-top">
          <span>FLOW CORE / 003</span>
          <strong>ACTIVE <i /></strong>
        </div>

        <svg className="vh-flow-mesh" viewBox="0 0 880 760" aria-hidden="true">
          <defs>
            <linearGradient id="vh-flow-gradient" x1="0" x2="1">
              <stop offset="0" stopColor={V.cyan} />
              <stop offset="0.48" stopColor={V.violet} />
              <stop offset="1" stopColor={V.pink} />
            </linearGradient>
            <radialGradient id="vh-core-gradient">
              <stop offset="0" stopColor={V.white} />
              <stop offset="0.25" stopColor={V.cyan} />
              <stop offset="0.7" stopColor={V.violet} stopOpacity="0.65" />
              <stop offset="1" stopColor={V.pink} stopOpacity="0" />
            </radialGradient>
            <filter id="vh-core-glow">
              <feGaussianBlur stdDeviation="18" />
            </filter>
          </defs>

          {Array.from({length: 7}).map((_, index) => {
            const y = 170 + index * 70;
            const amplitude = 34 + Math.sin((frame + index * 9) / 15) * 22;
            return (
              <path
                key={index}
                d={`M 35 ${y} C 185 ${y - amplitude}, 305 ${y + amplitude}, 440 ${y} S 690 ${y - amplitude}, 845 ${y}`}
                fill="none"
                stroke="url(#vh-flow-gradient)"
                strokeWidth={index === 3 ? 7 : 2.5}
                strokeLinecap="round"
                strokeDasharray={index === 3 ? "24 12" : "10 16"}
                strokeDashoffset={-(frame * (index === 3 ? 5 : 2.4) + index * 20)}
                opacity={0.32 + index * 0.07}
              />
            );
          })}

          <g transform={`rotate(${rotation} 440 380)`}>
            <circle cx="440" cy="380" r="205" fill="none" stroke={V.cyan} strokeWidth="2" opacity="0.28" strokeDasharray="12 20" />
            <circle cx="440" cy="175" r="10" fill={V.lime} />
          </g>
          <circle cx="440" cy="380" r={150 + Math.sin(frame / 9) * 8} fill="url(#vh-core-gradient)" opacity="0.9" filter="url(#vh-core-glow)" />
          <circle cx="440" cy="380" r="92" fill="none" stroke={V.white} strokeWidth="2" opacity="0.6" />
          <circle cx="440" cy="380" r="43" fill={V.white} opacity="0.9" />
        </svg>

        <div className="vh-flow-meters">
          <div><span>AMPLITUDE</span><strong>0.{Math.round(62 + Math.sin(frame / 10) * 16)}</strong><i><b style={{transform: `scaleX(${0.62 + Math.sin(frame / 10) * 0.16})`}} /></i></div>
          <div><span>VELOCITY</span><strong>{Math.round(124 + Math.sin(frame / 14) * 26)}</strong><i><b style={{transform: `scaleX(${0.74 + Math.sin(frame / 14) * 0.12})`}} /></i></div>
          <div><span>PHASE</span><strong>{Math.round(rotation)}°</strong><i><b style={{transform: `scaleX(${0.48 + Math.sin(frame / 16) * 0.18})`}} /></i></div>
        </div>
      </div>

      <div
        className="vh-flow-sweep"
        style={{
          transform: `translateX(${sweep}%) rotate(14deg)`,
        }}
      />

      <div className="vh-flow-tags">
        {["SYNCHRONIZED", "REAL-TIME", "ADAPTIVE"].map((item, index) => (
          <span
            className="vh-glass"
            key={item}
            style={{
              opacity: p(frame, 40 + index * 7, 65 + index * 7),
              transform: `translateY(${(1 - p(frame, 40 + index * 7, 65 + index * 7)) * 35}px)`,
            }}
          >
            {item}
          </span>
        ))}
      </div>
    </AbsoluteFill>
  );
};

const cursorPosition = (frame: number) => {
  const times = [0, 30, 70, 112, 155, 205, 248, 292, 338, 390, 445];
  return {
    x: interpolate(frame, times, [890, 155, 515, 800, 390, 890, 170, 560, 850, 320, 780], {...clamp, easing: travel}),
    y: interpolate(frame, times, [1660, 245, 590, 1030, 410, 1450, 490, 1150, 1510, 920, 1670], {...clamp, easing: travel}),
  };
};

const VerticalCursor: React.FC<{frame: number}> = ({frame}) => {
  const cursor = cursorPosition(frame);
  const clicks = [30, 70, 112, 155, 205, 248, 292, 338, 390, 445];
  const click = Math.max(
    ...clicks.map((at) => interpolate(frame, [at, at + 4, at + 10], [0, 1, 0], clamp)),
  );

  return (
    <>
      <div
        className="vh-click"
        style={{
          left: cursor.x,
          top: cursor.y,
          opacity: click,
          transform: `translate(-50%, -50%) scale(${0.3 + click * 1.2})`,
        }}
      />
      <div
        className="vh-cursor"
        style={{
          left: cursor.x,
          top: cursor.y,
          transform: `scale(${1 - click * 0.15})`,
        }}
      >
        <svg viewBox="0 0 48 56"><path d="M5 4 39 35 23 38 15 52Z" fill={V.white} stroke={V.night} strokeWidth="3" strokeLinejoin="round" /></svg>
        <span>DESIGNER</span>
      </div>
    </>
  );
};

const VerticalSelection: React.FC<{
  frame: number;
  range: [number, number];
  top: number;
  left: number;
  width: number;
  height: number;
  color: string;
  label: string;
}> = ({frame, range, top, left, width, height, color, label}) => {
  const opacity = interpolate(
    frame,
    [range[0] - 5, range[0], range[1], range[1] + 5],
    [0, 1, 1, 0],
    clamp,
  );

  return (
    <div className="vh-selection" style={{top, left, width, height, color, borderColor: color, opacity}}>
      <span>{label}</span><i /><i /><i /><i />
    </div>
  );
};

const DraggedService: React.FC<{frame: number}> = ({frame}) => {
  const drag = p(frame, 230, 292, travel);
  const settle = spring({
    frame: frame - 292,
    fps: 30,
    config: {damping: 19, stiffness: 108},
  });
  const start = {x: 68, y: 820};
  const end = {x: 535, y: 1170};

  return (
    <div
      className="vh-dragged-service"
      style={{
        opacity: p(frame, 224, 236),
        left: interpolate(drag, [0, 1], [start.x, end.x]),
        top: interpolate(drag, [0, 1], [start.y, end.y]),
        transform: `translate(-50%, -50%) scale(${drag < 1 ? 0.86 : 0.86 + settle * 0.14}) rotate(${(1 - drag) * -6}deg)`,
      }}
    >
      <i>04</i><strong>EXPERIENCE</strong><span>↗</span>
    </div>
  );
};

const VerticalDesign: React.FC<{frame: number}> = ({frame}) => {
  const opacity = sceneOpacity(frame, [0, 22], [430, 450]);
  const enter = spring({
    frame,
    fps: 30,
    config: {damping: 22, stiffness: 88, mass: 1},
  });
  const scroll = interpolate(frame, [180, 225, 300, 360, 440], [0, 0, 620, 1080, 1320], {
    ...clamp,
    easing: travel,
  });
  const typed = Math.floor(interpolate(frame, [32, 84], [0, "DESIGN WITHOUT LIMITS".length], clamp));
  const title = "DESIGN WITHOUT LIMITS".slice(0, typed);
  const image = p(frame, 72, 116);
  const services = p(frame, 190, 240);
  const testimonial = p(frame, 320, 370);
  const footer = p(frame, 378, 430);

  return (
    <AbsoluteFill style={{opacity, color: V.white, overflow: "hidden"}}>
      <VerticalPhoto src="backgrounds/light-gallery.png" frame={frame} opacity={0.42} />
      <div className="vh-scene-label">04 / LIVE MOBILE DESIGN</div>
      <div
        className="vh-workspace vh-glass"
        style={{
          opacity: enter,
          transform: `translateY(${(1 - enter) * 140}px) scale(${0.93 + enter * 0.07})`,
        }}
      >
        <div className="vh-workspace-top">
          <div><i /><i /><i /></div>
          <span>mobile-page / 1080 × 1920</span>
          <strong>100%</strong>
        </div>
        <div className="vh-toolbar">
          {["T", "□", "○", "↗", "✦"].map((tool) => <span key={tool}>{tool}</span>)}
          <div />
          {[V.cyan, V.violet, V.pink, V.lime].map((color) => <i key={color} style={{backgroundColor: color}} />)}
        </div>
        <div className="vh-mobile-frame">
          <div
            className="vh-site-scroll"
            style={{transform: `translateY(${-scroll}px)`}}
          >
            <div className="vh-site-hero">
              <nav><span>NORTH / STUDIO</span><strong>MENU +</strong></nav>
              <span className="vh-site-kicker">INDEPENDENT DIGITAL STUDIO</span>
              <h2>{title}<i /></h2>
              <p>Building sharp identities, interfaces and motion systems for the next generation.</p>
              <div className="vh-site-hero-image" style={{clipPath: `inset(${(1 - image) * 100}% 0 0 0 round 32px)`}}>
                <Img src={staticFile("backgrounds/glass-waves.png")} />
                <span>SELECTED / 2026</span>
              </div>
            </div>

            <section className="vh-site-services" style={{opacity: services}}>
              <span>02 / CAPABILITIES</span>
              <h3>Built as one<br /><em>connected system.</em></h3>
              <div className="vh-service-grid">
                {["STRATEGY", "IDENTITY", "MOTION"].map((item, index) => (
                  <article key={item}>
                    <span>0{index + 1}</span><strong>{item}</strong><i>↗</i>
                  </article>
                ))}
              </div>
            </section>

            <section className="vh-site-testimonial" style={{opacity: testimonial}}>
              <span>WHAT CLIENTS SAY</span>
              <blockquote>“Clear thinking, precise execution, and motion that made the whole identity feel alive.”</blockquote>
              <div><i /><strong>CREATIVE PARTNER / 2026</strong></div>
            </section>

            <section className="vh-site-footer" style={{opacity: footer}}>
              <span>HAVE A PROJECT?</span>
              <h3>LET’S MAKE<br />IT MOVE.</h3>
              <button type="button">START A CONVERSATION <b>↗</b></button>
            </section>
          </div>
        </div>
        <div className="vh-scrollbar"><i style={{transform: `translateY(${scroll * 0.19}px)`}} /></div>

        <VerticalSelection frame={frame} range={[22, 70]} top={245} left={145} width={610} height={175} color={V.cyan} label="HEADLINE / H1" />
        <VerticalSelection frame={frame} range={[70, 122]} top={480} left={129} width={750} height={390} color={V.pink} label="MEDIA / HERO" />
        <VerticalSelection frame={frame} range={[300, 352]} top={650} left={145} width={610} height={300} color={V.lime} label="SECTION / TESTIMONIAL" />
        <DraggedService frame={frame} />
        <VerticalCursor frame={frame} />
      </div>
    </AbsoluteFill>
  );
};

export const GlassHudVertical: React.FC = () => {
  const frame = useCurrentFrame();
  const end = interpolate(frame, [820, 840], [1, 0], {
    ...clamp,
    easing: Easing.in(Easing.cubic),
  });

  return (
    <AbsoluteFill style={{backgroundColor: V.night, color: V.white, opacity: end, overflow: "hidden"}}>
      <VerticalIntro frame={frame} />
      <VerticalCards frame={frame - 130} />
      <VerticalWaves frame={frame - 270} />
      <VerticalDesign frame={frame - 390} />
      <VerticalHud frame={frame} />
      <div className="vh-vignette" />
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
